import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { getApiErrorMessage } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { StarRating } from "@/components/StarRating";
import OrderBuilder from "@/components/OrderBuilder";
import IntakePanel from "@/components/IntakePanel";
import {
  buildWarmEmail,
  buildShortEmail,
  buildSms,
  emailSubjectFor,
  STATUS_ORDER,
  STATUS_LABELS,
} from "@/lib/templates";
import {
  X,
  Phone,
  ExternalLink,
  Copy as CopyIcon,
  Check,
  Mail,
  MessageSquare,
  UserPlus,
  Send,
  MapPin,
  Loader2,
  Sparkles,
  Package as PackageIcon,
} from "lucide-react";

export default function LeadDetailPanel({ placeId, onClose, onUpdated, users, currentUser }) {
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [tab, setTab] = useState("outreach"); // outreach | activity | preview
  const [iframeState, setIframeState] = useState("loading");

  const repName = currentUser?.name || "your name";

  const [warm, setWarm] = useState("");
  const [short, setShort] = useState("");
  const [sms, setSms] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [leadRes, actRes] = await Promise.all([
        api.get(`/leads/${encodeURIComponent(placeId)}`),
        api.get(`/leads/${encodeURIComponent(placeId)}/activities`),
      ]);
      const l = leadRes.data;
      setLead(l);
      setActivities(actRes.data || []);
      setWarm(buildWarmEmail(l, repName));
      setShort(buildShortEmail(l, repName));
      setSms(buildSms(l, repName));
      setIframeState("loading");
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [placeId, repName]);

  useEffect(() => {
    load();
  }, [load]);

  const regenerate = () => {
    if (!lead) return;
    setWarm(buildWarmEmail(lead, repName));
    setShort(buildShortEmail(lead, repName));
    setSms(buildSms(lead, repName));
    toast.success("Templates regenerated");
  };

  const updateStatus = async (nextStatus) => {
    if (!lead || nextStatus === lead.status) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/leads/${encodeURIComponent(placeId)}/status`, {
        status: nextStatus,
      });
      setLead(data);
      onUpdated && onUpdated(data);
      const acts = await api.get(`/leads/${encodeURIComponent(placeId)}/activities`);
      setActivities(acts.data || []);
      toast.success(`Status → ${STATUS_LABELS[nextStatus]}`);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const assignRep = async (repId) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/leads/${encodeURIComponent(placeId)}/assign`, {
        rep_id: repId || null,
      });
      setLead(data);
      onUpdated && onUpdated(data);
      const acts = await api.get(`/leads/${encodeURIComponent(placeId)}/activities`);
      setActivities(acts.data || []);
      toast.success(repId ? "Lead assigned" : "Lead unassigned");
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const submitNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    try {
      await api.post(`/leads/${encodeURIComponent(placeId)}/activities`, { message: note.trim() });
      setNote("");
      const acts = await api.get(`/leads/${encodeURIComponent(placeId)}/activities`);
      setActivities(acts.data || []);
      toast.success("Note added");
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const activeReps = useMemo(
    () => (users || []).filter((u) => u.role === "rep" && u.active),
    [users]
  );

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end"
      role="dialog"
      aria-modal="true"
      data-testid="lead-detail-panel"
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        data-testid="lead-detail-overlay"
      />
      {/* Panel */}
      <aside className="relative w-full max-w-[720px] bg-bg-surface border-l border-line h-full flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="border-b border-line px-6 py-4 shrink-0 flex items-start justify-between gap-4">
          {loading ? (
            <div className="flex items-center gap-2 text-ink-muted">
              <Loader2 size={14} className="animate-spin" />
              <span className="text-sm">Loading…</span>
            </div>
          ) : lead ? (
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">
                {lead.category || "Business"}
              </div>
              <h2 className="text-lg font-semibold text-ink tracking-tight mt-0.5 truncate" data-testid="lead-name">
                {lead.name}
              </h2>
              <div className="flex flex-wrap items-center gap-3 mt-2">
                <StatusPill status={lead.status} />
                <StarRating rating={lead.rating} reviews={lead.reviews} />
                <span className="text-xs text-ink-secondary flex items-center gap-1">
                  <MapPin size={11} className="text-ink-muted" />
                  {lead.city}
                </span>
                {lead.gap && (
                  <span
                    className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-line text-ink-muted"
                    data-testid="lead-gap"
                  >
                    {lead.gap === "no-site" ? "No site" : lead.gap}
                  </span>
                )}
              </div>
            </div>
          ) : null}
          <button
            onClick={onClose}
            data-testid="lead-detail-close-btn"
            className="text-ink-muted hover:text-ink hover:bg-bg-hover p-1.5 rounded-sm transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {loading ? (
          <div className="p-6 space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 border border-line rounded-sm animate-pulse bg-bg-hover" />
            ))}
          </div>
        ) : error ? (
          <div className="p-6" data-testid="lead-detail-error">
            <div className="text-sm text-destructive">{error}</div>
          </div>
        ) : lead ? (
          <>
            {/* Meta bar */}
            <div className="border-b border-line px-6 py-3 shrink-0 grid grid-cols-2 md:grid-cols-4 gap-4 bg-bg">
              <MetaCell label="Phone">
                {lead.phone ? (
                  <a
                    href={`tel:${lead.phone}`}
                    data-testid="click-to-call-link"
                    className="flex items-center gap-1.5 text-cyan hover:text-cyan-hover transition-colors font-mono text-sm tabular-nums"
                  >
                    <Phone size={12} />
                    {lead.phone}
                  </a>
                ) : (
                  <span className="text-ink-muted text-sm">—</span>
                )}
              </MetaCell>
              <MetaCell label="Segment">
                <span className="text-sm text-ink">{lead.segment || "—"}</span>
              </MetaCell>
              <MetaCell label="Assigned rep">
                <select
                  value={lead.assigned_rep_id || ""}
                  onChange={(e) => assignRep(e.target.value)}
                  disabled={saving}
                  data-testid="assign-rep-select"
                  className="bg-bg-surface border border-line rounded-sm px-2 py-1 text-sm text-ink focus:border-cyan transition-colors w-full"
                >
                  <option value="">Unassigned</option>
                  {activeReps.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </MetaCell>
              <MetaCell label="Sample">
                {lead.sample_url ? (
                  <a
                    href={lead.sample_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="open-sample-link"
                    className="inline-flex items-center gap-1.5 text-sm text-cyan hover:text-cyan-hover transition-colors"
                  >
                    <ExternalLink size={12} />
                    Open sample
                  </a>
                ) : (
                  <span className="text-sm text-ink-muted">Sample pending</span>
                )}
              </MetaCell>
            </div>

            {/* Pipeline steps */}
            <div className="border-b border-line px-6 py-3 shrink-0">
              <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-2">
                Pipeline
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="pipeline-steps">
                {STATUS_ORDER.map((s) => {
                  const isCurrent = s === lead.status;
                  return (
                    <button
                      key={s}
                      onClick={() => updateStatus(s)}
                      disabled={saving || isCurrent}
                      data-testid={`pipeline-step-${s}`}
                      className={`text-xs px-2.5 py-1 rounded-sm border transition-colors ${
                        isCurrent
                          ? "border-cyan bg-cyan-muted text-ink"
                          : "border-line text-ink-secondary hover:bg-bg-hover hover:text-ink"
                      }`}
                    >
                      {STATUS_LABELS[s]}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-line px-6 shrink-0 flex gap-6 overflow-x-auto" data-testid="lead-detail-tabs">
              <TabBtn active={tab === "outreach"} onClick={() => setTab("outreach")} testId="tab-outreach">
                Outreach
              </TabBtn>
              <TabBtn active={tab === "order"} onClick={() => setTab("order")} testId="tab-order">
                Order
              </TabBtn>
              <TabBtn active={tab === "intake"} onClick={() => setTab("intake")} testId="tab-intake">
                <span className="flex items-center gap-1.5">
                  Intake
                  {lead.customized && (
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: "#22D3C0" }}
                    />
                  )}
                </span>
              </TabBtn>
              <TabBtn active={tab === "preview"} onClick={() => setTab("preview")} testId="tab-preview">
                Sample preview
              </TabBtn>
              <TabBtn active={tab === "activity"} onClick={() => setTab("activity")} testId="tab-activity">
                Activity{activities.length > 0 ? ` (${activities.length})` : ""}
              </TabBtn>
            </div>

            {/* Tab content */}
            <div className="flex-1 overflow-y-auto">
              {tab === "outreach" && (
                <OutreachTab
                  lead={lead}
                  warm={warm}
                  setWarm={setWarm}
                  short={short}
                  setShort={setShort}
                  sms={sms}
                  setSms={setSms}
                  onRegenerate={regenerate}
                  onAfterSend={(kind) => {
                    if (lead.status === "new" && (kind === "warm" || kind === "short")) {
                      updateStatus("emailed");
                    }
                  }}
                />
              )}
              {tab === "order" && (
                <OrderBuilder
                  placeId={lead.place_id}
                  onOrderChanged={async () => {
                    // Reload lead + activities to reflect status transitions
                    try {
                      const [leadRes, actRes] = await Promise.all([
                        api.get(`/leads/${encodeURIComponent(placeId)}`),
                        api.get(`/leads/${encodeURIComponent(placeId)}/activities`),
                      ]);
                      setLead(leadRes.data);
                      setActivities(actRes.data || []);
                      onUpdated && onUpdated(leadRes.data);
                    } catch (e) {
                      /* silent */
                    }
                  }}
                />
              )}
              {tab === "intake" && <IntakePanel placeId={lead.place_id} />}
              {tab === "preview" && (
                <SamplePreview
                  url={lead.sample_url}
                  state={iframeState}
                  setState={setIframeState}
                />
              )}
              {tab === "activity" && (
                <ActivityTab
                  activities={activities}
                  note={note}
                  setNote={setNote}
                  onSubmit={submitNote}
                />
              )}
            </div>
          </>
        ) : null}
      </aside>
    </div>
  );
}

function MetaCell({ label, children }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">
        {label}
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`text-sm py-3 border-b-2 -mb-px transition-colors ${
        active
          ? "border-cyan text-ink"
          : "border-transparent text-ink-secondary hover:text-ink"
      }`}
    >
      {children}
    </button>
  );
}

function OutreachTab({ lead, warm, setWarm, short, setShort, sms, setSms, onRegenerate, onAfterSend }) {
  const subject = emailSubjectFor(lead);
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-medium text-ink">Copy-ready outreach</h3>
          <p className="text-xs text-ink-muted mt-1">
            Personalized from lead fields. Edit inline — nothing is auto-sent in v1.
          </p>
        </div>
        <button
          onClick={onRegenerate}
          data-testid="regenerate-templates-btn"
          className="text-xs border border-line text-ink-secondary hover:text-ink hover:bg-bg-hover px-3 py-1.5 rounded-sm transition-colors"
        >
          Regenerate
        </button>
      </div>

      <OutreachBox
        icon={Mail}
        label="Warm email"
        subject={subject}
        value={warm}
        onChange={setWarm}
        toEmail=""
        copyId="warm-email"
        onSent={() => onAfterSend("warm")}
      />

      <OutreachBox
        icon={Send}
        label="Short email"
        subject={subject}
        value={short}
        onChange={setShort}
        toEmail=""
        copyId="short-email"
        onSent={() => onAfterSend("short")}
      />

      <OutreachBox
        icon={MessageSquare}
        label="SMS"
        subject={null}
        value={sms}
        onChange={setSms}
        toPhone={lead.phone}
        copyId="sms"
        onSent={() => onAfterSend("sms")}
      />
    </div>
  );
}

function OutreachBox({ icon: Icon, label, subject, value, onChange, toEmail, toPhone, copyId, onSent }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      toast.error("Clipboard blocked — select and copy manually");
    }
  };

  const openInClient = () => {
    if (toPhone) {
      const smsHref = `sms:${toPhone}?body=${encodeURIComponent(value)}`;
      window.location.href = smsHref;
      onSent && onSent();
    } else {
      const mailtoHref = `mailto:${toEmail || ""}?subject=${encodeURIComponent(subject || "")}&body=${encodeURIComponent(value)}`;
      window.location.href = mailtoHref;
      onSent && onSent();
    }
  };

  return (
    <div className="border border-line rounded-sm bg-bg" data-testid={`outreach-box-${copyId}`}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-line bg-bg-surface">
        <div className="flex items-center gap-2 text-sm text-ink">
          <Icon size={13} className="text-cyan" />
          <span className="font-medium">{label}</span>
          {subject && (
            <span className="text-[11px] text-ink-muted font-mono ml-1 truncate">
              subject: {subject}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={copy}
            data-testid={`copy-${copyId}-btn`}
            className="flex items-center gap-1.5 text-xs border border-line text-ink hover:bg-bg-hover px-2.5 py-1 rounded-sm transition-colors"
          >
            {copied ? <Check size={12} className="text-cyan" /> : <CopyIcon size={12} />}
            {copied ? "Copied" : "Copy"}
          </button>
          <button
            onClick={openInClient}
            data-testid={`send-${copyId}-btn`}
            className="flex items-center gap-1.5 text-xs bg-cyan text-bg font-medium hover:bg-cyan-hover px-2.5 py-1 rounded-sm transition-colors active:scale-[0.98]"
          >
            {toPhone ? <MessageSquare size={12} /> : <Mail size={12} />}
            {toPhone ? "Open in SMS" : "Open in mail"}
          </button>
        </div>
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`outreach-textarea-${copyId}`}
        rows={label === "SMS" ? 4 : 12}
        spellCheck={false}
        className="w-full bg-bg text-ink text-sm font-mono px-3 py-3 resize-y focus:outline-none whitespace-pre-wrap leading-relaxed"
      />
    </div>
  );
}

function SamplePreview({ url, state, setState }) {
  if (!url) {
    return (
      <div className="p-6" data-testid="sample-preview-pending">
        <div className="border border-line rounded-sm p-6 bg-bg-surface max-w-md">
          <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted mb-1">
            Sample pending
          </div>
          <div className="text-sm text-ink">
            No live sample has been built for this lead yet. Build one before
            sending outreach — the whole system depends on leading with the gift.
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="p-6 h-full flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">
          Live preview
        </div>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-cyan hover:text-cyan-hover flex items-center gap-1 transition-colors"
          data-testid="preview-open-btn"
        >
          <ExternalLink size={11} />
          Open in new tab
        </a>
      </div>
      <div className="relative flex-1 min-h-[420px] border border-line rounded-sm bg-bg overflow-hidden">
        {state === "loading" && (
          <div className="absolute inset-0 flex items-center justify-start px-6 gap-2 text-ink-muted text-sm">
            <Loader2 size={14} className="animate-spin" />
            Loading preview…
          </div>
        )}
        {state === "error" && (
          <div className="absolute inset-0 flex flex-col items-start justify-start p-6 text-ink-secondary text-sm gap-2">
            <div className="text-destructive">Preview blocked by the target site.</div>
            <div className="text-xs text-ink-muted">
              Some hosts refuse iframe embedding. Use &ldquo;Open in new tab&rdquo; instead.
            </div>
          </div>
        )}
        <iframe
          key={url}
          src={url}
          title="Sample preview"
          onLoad={() => setState("loaded")}
          onError={() => setState("error")}
          data-testid="sample-preview-iframe"
          className="w-full h-full"
          style={{ display: state === "error" ? "none" : "block", minHeight: 420 }}
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
        />
      </div>
    </div>
  );
}

function ActivityTab({ activities, note, setNote, onSubmit }) {
  return (
    <div className="p-6">
      <form onSubmit={onSubmit} className="mb-6" data-testid="add-note-form">
        <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted mb-1.5">
          Log a note
        </div>
        <div className="flex gap-2">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Left a voicemail; owner said call back Thursday…"
            data-testid="note-input"
            className="flex-1 bg-bg border border-line rounded-sm px-3 py-2 text-sm text-ink placeholder:text-ink-muted focus:border-cyan transition-colors"
          />
          <button
            type="submit"
            data-testid="add-note-btn"
            disabled={!note.trim()}
            className="flex items-center gap-1.5 text-sm bg-cyan text-bg font-medium hover:bg-cyan-hover px-3 py-2 rounded-sm transition-colors disabled:opacity-40 active:scale-[0.98]"
          >
            <UserPlus size={13} />
            Add
          </button>
        </div>
      </form>

      {activities.length === 0 ? (
        <div className="border border-line rounded-sm p-6 bg-bg-surface text-sm text-ink-muted" data-testid="activity-empty">
          No activity yet.
        </div>
      ) : (
        <div className="border border-line rounded-sm bg-bg-surface divide-y divide-line" data-testid="activity-list">
          {activities.map((a) => (
            <div key={a.id} className="px-4 py-3">
              <div className="flex items-baseline justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm text-ink">{a.message}</div>
                  <div className="text-[11px] text-ink-muted mt-0.5 font-mono">
                    {a.user_name}{" "}
                    <span className="text-ink-muted">·</span>{" "}
                    <ActivityType type={a.type} />
                  </div>
                </div>
                <div className="text-[11px] text-ink-muted tabular-nums shrink-0 font-mono">
                  {formatTs(a.timestamp)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ActivityType({ type }) {
  const map = {
    status_change: "status",
    assignment: "assignment",
    note: "note",
  };
  return <span className="uppercase tracking-wider">{map[type] || type}</span>;
}

function formatTs(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
